/*
 * Copyright © 2025 AionTrack. All rights reserved.
 */

import ExcelJS from 'exceljs'

export interface ParsedClip {
  name: string
  tape_name?: string
  source_timecode?: string
  duration_frames?: number
  fps?: number
  source_file_path?: string
  tags?: string[]
  custom_metadata?: Record<string, any>
}

export interface ExcelParseResult {
  success: boolean
  clips: ParsedClip[]
  errors: string[]
  totalRows: number
  parsedRows: number
}

/**
 * Parse Excel files (XLSX, XLS, CSV) for VFX submissions and other clip data
 *
 * Supports various Excel formats and automatically detects headers.
 * Maps common VFX submission columns to clip data structure.
 *
 * Example columns:
 * - Vendor, Shot Number, Submission Name, File Type, Resolution, Notes, etc.
 * - Any standard ALE-style columns (Name, Tape, Start, Duration, FPS, etc.)
 */
export async function parseExcelFile(file: File): Promise<ExcelParseResult> {
  const errors: string[] = []
  const clips: ParsedClip[] = []

  try {
    // Read the file as an array buffer
    const arrayBuffer = await file.arrayBuffer()

    // Create workbook and load from buffer
    const workbook = new ExcelJS.Workbook()

    // Determine file type and parse accordingly
    const filename = file.name.toLowerCase()
    if (filename.endsWith('.csv')) {
      const textDecoder = new TextDecoder()
      const csvText = textDecoder.decode(arrayBuffer)
      await workbook.csv.read(csvText)
    } else {
      await workbook.xlsx.load(arrayBuffer)
    }

    // Get the first sheet
    const worksheet = workbook.worksheets[0]
    if (!worksheet || worksheet.rowCount === 0) {
      return {
        success: false,
        clips: [],
        errors: ['Excel file has no sheets or is empty'],
        totalRows: 0,
        parsedRows: 0,
      }
    }

    // Get headers from first row
    const headerRow = worksheet.getRow(1)
    const headers: string[] = []
    headerRow.eachCell((cell, colNumber) => {
      headers[colNumber] = String(cell.value ?? '').trim()
    })

    if (headers.filter(h => h).length === 0) {
      return {
        success: false,
        clips: [],
        errors: ['Excel file has no headers'],
        totalRows: 0,
        parsedRows: 0,
      }
    }

    // Normalize headers for mapping
    const headerMap = normalizeHeaders(headers.filter(h => h))

    let totalRows = 0
    let parsedRows = 0

    // Process each row (starting from row 2, after headers)
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return // Skip header row

      totalRows++

      try {
        const clip: ParsedClip = {
          name: '',
          custom_metadata: {},
        }

        // Build row data object
        const rowData: Record<string, any> = {}
        row.eachCell((cell, colNumber) => {
          const header = headers[colNumber]
          if (header) {
            rowData[header] = getCellValue(cell)
          }
        })

        // Map values to clip properties
        for (const header of Object.keys(rowData)) {
          const value = rowData[header]
          const mappedField = headerMap.get(header)

          const isEmpty = value === null || value === undefined || value === ''

          // Map to core database fields
          if (mappedField === 'name' && !isEmpty) {
            clip.name = String(value)
          } else if (mappedField === 'tape_name' && !isEmpty) {
            clip.tape_name = String(value)
          } else if (mappedField === 'source_timecode' && !isEmpty) {
            clip.source_timecode = String(value)
          } else if (mappedField === 'duration_frames' && !isEmpty) {
            const frames = parseFramesFromDuration(String(value))
            if (frames !== null) clip.duration_frames = frames
          } else if (mappedField === 'fps' && !isEmpty) {
            const fps = typeof value === 'number' ? value : parseFloat(String(value))
            if (!isNaN(fps) && fps > 0) clip.fps = fps
          } else if (mappedField === 'source_file_path' && !isEmpty) {
            clip.source_file_path = String(value)
          }

          // Store ALL columns in custom_metadata
          if (!isEmpty) {
            if (!clip.custom_metadata) clip.custom_metadata = {}
            if (value instanceof Date) {
              clip.custom_metadata[header] = value.toISOString()
            } else {
              clip.custom_metadata[header] = value
            }
          }
        }

        // Validate required fields
        if (!clip.name) {
          errors.push(`Row ${rowNumber}: Missing clip name`)
          return
        }

        // Default tape_name to clip name if not set
        if (!clip.tape_name) {
          clip.tape_name = clip.name
        }

        clips.push(clip)
        parsedRows++
      } catch (error: any) {
        errors.push(`Row ${rowNumber}: ${error.message}`)
      }
    })

    return {
      success: clips.length > 0,
      clips,
      errors,
      totalRows,
      parsedRows,
    }
  } catch (error: any) {
    return {
      success: false,
      clips: [],
      errors: [`Failed to parse Excel file: ${error.message}`],
      totalRows: 0,
      parsedRows: 0,
    }
  }
}

/**
 * Extract the actual value from an ExcelJS cell
 */
function getCellValue(cell: ExcelJS.Cell): any {
  const value = cell.value

  if (value === null || value === undefined) {
    return ''
  }

  // Handle rich text
  if (typeof value === 'object' && 'richText' in value) {
    return value.richText.map((rt: any) => rt.text).join('')
  }

  // Handle formulas - return the result
  if (typeof value === 'object' && 'result' in value) {
    return value.result
  }

  // Handle hyperlinks
  if (typeof value === 'object' && 'text' in value) {
    return value.text
  }

  // Handle dates
  if (value instanceof Date) {
    return value
  }

  return value
}

/**
 * Normalize column headers to standard field names
 * Supports common variations of column names across different systems
 * Including VFX-specific columns from submission spreadsheets
 */
function normalizeHeaders(headers: string[]): Map<string, string> {
  const map = new Map<string, string>()

  headers.forEach(header => {
    const lower = header.toLowerCase().trim()

    // Clean up common prefixes from database exports (e.g., "Submissions_View::")
    const cleanHeader = lower.replace(/.*::/, '').replace(/_/g, ' ').trim()

    // Name variations - map submission name/filename to name
    if (lower === 'name' || lower === 'clip name' || lower === 'clipname' ||
        cleanHeader === 'submission name' || cleanHeader === 'file name' ||
        cleanHeader === 'filename') {
      map.set(header, 'name')
    }
    // Tape name variations - for VFX submissions, tape name should be the filename
    // NOT the shot number (shot number stays in custom_metadata only)
    else if (lower === 'tape' || lower === 'tape name' || lower === 'tapename' ||
             lower === 'reel') {
      map.set(header, 'tape_name')
    }
    // Timecode variations
    else if (lower === 'start' || lower === 'source in' || lower === 'source_in' ||
             lower === 'source timecode' || lower === 'source_timecode' ||
             lower === 'tc in' || lower === 'tc_in' || lower === 'timecode') {
      map.set(header, 'source_timecode')
    }
    // Duration variations
    else if (lower === 'duration' || lower === 'length') {
      map.set(header, 'duration_frames')
    }
    // FPS variations
    else if (lower === 'fps' || lower === 'frame rate' || lower === 'framerate') {
      map.set(header, 'fps')
    }
    // Source file variations
    else if (lower === 'source file' || lower === 'source_file' ||
             lower === 'file path' || lower === 'filepath' ||
             cleanHeader === 'submission file' || cleanHeader === 'file') {
      map.set(header, 'source_file_path')
    }
  })

  return map
}

/**
 * Parse duration from timecode format (HH:MM:SS:FF) to total frames
 * Also handles simple frame count values
 */
function parseFramesFromDuration(duration: string): number | null {
  // If it's just a number, treat it as frames
  const simpleFrames = parseInt(duration, 10)
  if (!isNaN(simpleFrames) && duration === simpleFrames.toString()) {
    return simpleFrames
  }

  // Parse timecode format HH:MM:SS:FF
  const parts = duration.split(':')
  if (parts.length === 4) {
    const [hours, minutes, seconds, frames] = parts.map(p => parseInt(p, 10))

    if (!isNaN(hours) && !isNaN(minutes) && !isNaN(seconds) && !isNaN(frames)) {
      // Assume 24fps for now - this could be improved by reading FPS from the file
      const fps = 24
      return (hours * 3600 + minutes * 60 + seconds) * fps + frames
    }
  }

  return null
}

/**
 * Validate file is an Excel file
 */
export function isValidExcelFile(filename: string): boolean {
  const lower = filename.toLowerCase()
  return lower.endsWith('.xlsx') || lower.endsWith('.xls') || lower.endsWith('.csv')
}
